import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { DeviceEnrichmentMap } from '../types';

/**
 * FS-891 — firebase-api answers both enrichment reads only for read_devices.
 * Pages that show the records as optional detail under their own permission
 * (Alerts, Firmware) keep working without it and show this line instead.
 */
export const ENRICHMENT_NEEDS_READ_DEVICES = 'Homeowner details need the read_devices permission.';

/** The status of a failed read, when the server answered. */
export const enrichmentErrorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * Everything on the page that reads the app's own records.
 *
 * The enrichment used to be read once per panel and left alone, which was fine
 * while only the MyFluxHive app wrote it. The console writes it now — an
 * operator attaches a label photo in the equipment drawer, and the server
 * fills fields off it — so a panel that read it on mount is showing yesterday.
 * Anything that changes those records calls `refreshDeviceEnrichment()`, and
 * every live reader fetches again.
 */
const refreshListeners = new Set<() => void>();

/** Tell every enrichment reader on the page to read again. */
export function refreshDeviceEnrichment(): void {
  refreshListeners.forEach((listener) => listener());
}

/** Listen for that. Returns the unsubscribe, for an effect cleanup. */
export function subscribeToEnrichmentRefresh(listener: () => void): () => void {
  refreshListeners.add(listener);
  return () => {
    refreshListeners.delete(listener);
  };
}

export function useFirestoreEnrichment() {
  const { id: accountId, hasPermission } = useAuth();
  const canRead = hasPermission('read_devices');
  const [enrichmentMap, setEnrichmentMap] = useState<DeviceEnrichmentMap>({});
  const [loading, setLoading] = useState(canRead);
  const [error, setError] = useState<string | null>(null);
  /** Set when firebase-api refused the read (403), so the page can say why. */
  const [refused, setRefused] = useState(false);
  /** Only the newest request may land: an answer for an earlier account or access is dropped. */
  const latest = useRef(0);

  /**
   * A refresh never drops back to loading: the first read has nothing to show
   * and says so, but a refresh happens under photos that are already on the
   * screen, and blinking them out for a spinner is worse than a second's wait.
   */
  const fetchEnrichment = useCallback(async () => {
    if (!canRead) return;
    const ticket = ++latest.current;
    try {
      const response = await axiosInstance.get<DeviceEnrichmentMap>(
        `${config.firebaseApiUrl}/fb/devices/enrich`
      );
      if (ticket !== latest.current) return;
      setEnrichmentMap(response.data);
      setError(null);
      setRefused(false);
    } catch (err) {
      if (ticket !== latest.current) return;
      console.error('Error fetching Firestore enrichment:', err);
      const status = enrichmentErrorStatus(err);
      // FS-891: a refusal means this session may no longer see what it read
      // before, so it goes. Anything else keeps the records on screen.
      if (status === 401 || status === 403) setEnrichmentMap({});
      if (status === 403) {
        setRefused(true);
        setError(null);
      } else {
        setError('Failed to fetch Firestore data');
      }
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, [canRead]);

  // A new account, or a change in read access, starts over: nothing read
  // under the old one stays on screen, and its answers still in flight are
  // dropped. Without read_devices nothing is asked for.
  useEffect(() => {
    latest.current += 1;
    setEnrichmentMap({});
    setError(null);
    setRefused(false);
    setLoading(canRead);
    fetchEnrichment();
  }, [fetchEnrichment, canRead, accountId]);

  useEffect(() => subscribeToEnrichmentRefresh(() => void fetchEnrichment()), [fetchEnrichment]);

  return {
    enrichmentMap,
    loading,
    error,
    unavailableReason: !canRead || refused ? ENRICHMENT_NEEDS_READ_DEVICES : null,
    refresh: () => fetchEnrichment(),
  };
}
